/** @jsxImportSource ./simboluri-jsx */
// «Lanțul de calcul» — o VEDERE a structurii (A2), nu o pagină a șinei
// (mock-ui/design/Pipeline.dc.html): pașii în ordinea de RULARE, cu singurul
// buton de execuție. Butonul pornește FAZA rândului privit (contract §5.1):
// «Reia schița» pe schiță, «Rerulează» pe o geometrie macro generată,
// «Pornește calculul detaliat» pe un detaliu sau pe o macro editată. Ce
// pornește și dacă se poate hotărăște `nav/selectie.actiuneaLantului` (un
// detaliu în lucru sau gata nu se reia de aici), iar pornirea trece prin
// `nav/actiuni`, care arată întâi ce se șterge (A1).

import { STEPS, hashSectiune, titluPas } from "./legaturi";
import { useT } from "../i18n";
import { useRolProiect } from "../components/rol";
import { CAPITOLE } from "./capitole";
import { etapaTitlu, lantInLucru, RaportLant, StepState } from "./tipuri";

export default function PipelinePage({
  id,
  solutie,
  steps,
  runningAll,
  runError,
  etichetaRulare,
  ruleaza,
  onRunAll,
  onStop,
  lant,
}: {
  id: string;
  solutie?: string | null;
  steps: Record<string, StepState>;
  runningAll: boolean;
  runError: string | null;
  /** ce pornește butonul pe rândul privit; `null` = nimic de pornit */
  etichetaRulare: "reia_schita" | "reruleaza" | "porneste_detaliat" | null;
  /** rândul are o rulare deschisă: butonul devine ■ */
  ruleaza: boolean;
  onRunAll: () => void;
  onStop: () => void;
  lant: RaportLant | null;
}) {
  const { potScrie } = useRolProiect();
  const t = useT();
  const lantRuleaza = lantInLucru(lant);
  return (
    <div className="ob-pagina ob-pagina-ingusta">
      <div className="ob-pipe-head">
        <h2>{t("struct.pipe.titlu")}</h2>
        <span className="ob-chip ob-c-mut">{t("struct.pipe.ordine")}</span>
        {/* Harta capitolelor se deschide DE AICI — așa promitea antetul
            componentei, dar linkul nu exista nicăieri, deci pagina „Acasă"
            era de neatins. */}
        <a
          className="ob-chip ob-c-acc"
          style={{ textDecoration: "none" }}
          href={hashSectiune(id, "acasa", solutie ?? null)}
          title={t("struct.pipe.harta_titlu")}
        >
          {t("struct.pipe.harta")}
        </a>
        <span style={{ flex: 1 }} />
        {!potScrie ? null : ruleaza || lantRuleaza ? (
          <button className="pj-run" onClick={onStop} title={t("struct.pipe.opreste_titlu")}>
            {t("struct.pipe.opreste")}
          </button>
        ) : etichetaRulare ? (
          <button
            className="pj-run"
            disabled={runningAll}
            onClick={onRunAll}
          >
            {t(`actiune.${etichetaRulare}` as const)}
          </button>
        ) : null}
      </div>
      {runError && <div className="sg-error">{runError}</div>}

      {/* Ordinea rămâne cea de RULARE, dar pașii se citesc pe capitole: fără
          antete, cei 19 pași erau o listă plată de nume, iar `capitole.ts`
          promitea de la început că „gruparea din Pipeline" se desenează din
          el. */}
      <div className="ob-pipe-lista">
        {CAPITOLE.map((cap) => (
          <div key={cap.nr}>
            <div className="ob-pipe-cap">
              <span className="ob-lbl">{t("struct.pipe.capitolul", { nr: cap.nr })}</span>
              <b style={{ fontSize: 13 }}>{t(cap.titlu)}</b>
              <span style={{ fontSize: 12, color: "var(--mut)" }}>
                {t(cap.intrebare)}
              </span>
            </div>
            {randuriPasi(cap.pasi)}
          </div>
        ))}
      </div>

      {/* the Reset page was dropped in the platform merge: every run starts
          from scratch anyway, so the page had nothing left to reset */}
      <div style={{ marginTop: 22, display: "flex", gap: 16, fontSize: 12 }}>
        <a href={hashSectiune(id, "imbunatatiri", solutie ?? null)}>
          {t("struct.pipe.imbunatatiri")}
        </a>
      </div>
    </div>
  );

  function randuriPasi(chei: string[]) {
    return chei
      .map((cheie) => STEPS.find((x) => x.key === cheie))
      .filter((x): x is (typeof STEPS)[number] => !!x)
      .map((s) => {
          const st = steps[s.key];
          // fiecare pas rulat are rândul lui de progres pe server
          const isRunning = !!st?.progres;
          // Pasul 17 rulează și fără reacțiuni: rămâne „în așteptare" și
          // poartă mai departe predimensionarea. Verde pe listă, dar fără
          // verdicte — iar asta se spune, nu se lasă ghicit.
          const faza6 =
            s.key !== "verificare_fundatie" || !st?.result
              ? null
              : (st.result as { verificari?: { stare?: string } }).verificari
                    ?.stare === "calculat"
                ? null
                : t("struct.pipe.fara_reactiuni");
          return (
            <div key={s.key}>
              <a
                className="ob-pipe-pas"
                href={hashSectiune(id, s.key, solutie ?? null)}
              >
                <span
                  className={`ob-dot ${
                    isRunning ? "running" : faza6 ? "partial" : st?.status ?? ""
                  }`}
                />
                <span style={{ flex: 1 }}>{titluPas(s.key)}</span>
                {isRunning && (
                  <span
                    className="ob-chip ob-c-acc"
                    title={t("struct.pipe.ruleaza_titlu")}
                  >
                    {t("struct.pipe.ruleaza")}
                  </span>
                )}
                {faza6 && (
                  <span
                    className="ob-chip ob-c-amb"
                    title={t("struct.pipe.fara_reactiuni_titlu")}
                  >
                    {faza6}
                  </span>
                )}
              </a>
              {st?.progres ? (
                <div className="ob-pipe-etapa">
                  <b>{etapaTitlu(st.progres)}</b> —{" "}
                  {st.progres.detaliu?.acum ?? st.progres.etapa}
                </div>
              ) : null}
            </div>
          );
      });
  }
}
